import * as React from "react";
import { Layers, Plus, Trash2 } from "lucide-react";
import { Button } from "../ui/Button";
import { useSpaceStore } from "../../stores/spaceStore";
import { cn } from "../../lib/utils";
import type { Surface } from "../../types";

interface SurfaceListProps {
  onAddSurface?: () => void;
  onDeleteSurface?: (surface: Surface) => void;
}

export function SurfaceList({ onAddSurface, onDeleteSurface }: SurfaceListProps) {
  const { surfaces, selectedSurface, setSelectedSurface } = useSpaceStore();

  const surfacesByType = React.useMemo(() => {
    const grouped: Record<string, Surface[]> = {
      floor: [],
      walls: [],
      ceiling: [],
      stairs: [],
    };

    surfaces.forEach((surface) => {
      if (grouped[surface.type]) {
        grouped[surface.type].push(surface);
      }
    });

    return grouped;
  }, [surfaces]);

  const getSurfaceIcon = (type: string) => {
    switch (type) {
      case "floor":
        return "⬛";
      case "walls":
        return "🧱";
      case "ceiling":
        return "⬜";
      case "stairs":
        return "🪜";
      default:
        return "📦";
    }
  };

  const getSurfaceColor = (type: string) => {
    switch (type) {
      case "floor":
        return "bg-warning/20 border-warning/50 text-warning dark:text-warning";
      case "walls":
        return "bg-blue-500/20 border-blue-500/50 text-blue-700 dark:text-blue-300";
      case "ceiling":
        return "bg-purple-500/20 border-purple-500/50 text-purple-700 dark:text-purple-300";
      case "stairs":
        return "bg-emerald-500/20 border-emerald-500/50 text-emerald-700 dark:text-emerald-300";
      default:
        return "bg-gray-500/20 border-gray-500/50";
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="flex items-center gap-2 font-semibold">
          <Layers className="h-4 w-4" />
          Surfaces
        </h3>
        {onAddSurface && (
          <Button variant="outline" size="sm" onClick={onAddSurface}>
            <Plus className="h-4 w-4 mr-1" />
            Add
          </Button>
        )}
      </div>

      <div className="space-y-3">
        {Object.entries(surfacesByType).map(([type, typeSurfaces]) => (
          typeSurfaces.length > 0 && (
            <div key={type} className="space-y-1">
              <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
                {type}
              </p>
              <div className="space-y-1">
                {typeSurfaces.map((surface) => (
                  <div
                    key={surface.label}
                    className={cn(
                      "group flex items-center justify-between rounded-lg border p-2 cursor-pointer transition-all",
                      getSurfaceColor(surface.type),
                      selectedSurface?.label === surface.label
                        ? "ring-2 ring-primary ring-offset-2"
                        : "hover:bg-accent"
                    )}
                    onClick={() => setSelectedSurface(surface)}
                  >
                    <div className="flex items-center gap-2">
                      <span className="text-lg">{getSurfaceIcon(surface.type)}</span>
                      <span className="font-medium">{surface.label}</span>
                    </div>

                    {onDeleteSurface && (
                      <Button
                        variant="ghost"
                        size="icon"
                        aria-label="Delete surface"
                        className="h-7 w-7 opacity-0 group-hover:opacity-100 transition-opacity"
                        onClick={(e) => {
                          e.stopPropagation();
                          onDeleteSurface(surface);
                        }}
                      >
                        <Trash2 className="h-4 w-4 text-destructive" />
                      </Button>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )
        ))}

        {surfaces.length === 0 && (
          <p className="text-center text-sm text-muted-foreground py-8">
            No surfaces loaded. Load a space first.
          </p>
        )}
      </div>
    </div>
  );
}
